import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material';

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import ArtTrackOutlinedIcon from '@mui/icons-material/ArtTrackOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';

const ACCENT = '#004591';

const options = [
  {
    title: 'Laminario',
    description: 'Accede al laminario de imágenes para gestionar y visualizar imágenes anatómicas',
    icon: <ArtTrackOutlinedIcon sx={{ fontSize: 40 }} />,
    path: '/resources/laminaria',
  },
  {
    title: 'Videos',
    description: 'Explora y gestiona los videos educativos disponibles',
    icon: <VideoLibraryOutlinedIcon sx={{ fontSize: 40 }} />,
    path: '/resources/videos',
  },
  {
    title: 'Estructuras Anatómicas 3D',
    description: 'Accede a las estructuras anatómicas 3D para visualizar y gestionar modelos 3D',
    icon: <AccessibilityIcon sx={{ fontSize: 40 }} />,
    path: '/resources/anatomical-collections',
  },
];

const Resources = () => {
  const navigate = useNavigate();
  const handleOptionClick = path => {
    navigate(path);
  };

  return (
    <Stack direction='column' spacing={1} p={{ xs: 3, md: 5 }} alignItems='center'>
      <Typography variant='h4' fontWeight='bold' color='primary'>
        Recursos
      </Typography>
      <Typography variant='body1' color='text.secondary' sx={{ mb: 3, textAlign: 'center' }}>
        Selecciona una opción para acceder a los distintos recursos disponibles en la plataforma.
        Puedes explorar el laminario de imágenes, gestionar videos educativos o acceder a las
        estructuras anatómicas 3D.
      </Typography>
      <Grid container spacing={3} justifyContent='center' sx={{ maxWidth: 1100 }}>
        {options.map((option, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Paper
              elevation={0}
              onClick={() => handleOptionClick(option.path)}
              sx={{
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'rgba(0, 69, 145, 0.12)',
                backgroundColor: '#ffffff',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                // Barra de acento superior
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 5,
                  backgroundColor: ACCENT,
                  transform: 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.3s ease',
                },
                '&:hover': {
                  transform: 'translateY(-6px)',
                  borderColor: ACCENT,
                  boxShadow: `0 18px 34px -18px ${ACCENT}`,
                },
                '&:hover::before': {
                  transform: 'scaleX(1)',
                },
                '&:hover .option-icon': {
                  transform: 'scale(1.08)',
                  backgroundColor: ACCENT,
                  color: '#ffffff',
                },
                '&:hover .option-cta': {
                  gap: 1.5,
                },
              }}>
              <Stack direction='column' spacing={2} alignItems='center' p={4} sx={{ flexGrow: 1 }}>
                <Box
                  className='option-icon'
                  sx={{
                    width: 76,
                    height: 76,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: ACCENT,
                    backgroundColor: `${ACCENT}18`,
                    transition:
                      'transform 0.25s ease, background-color 0.25s ease, color 0.25s ease',
                  }}>
                  {option.icon}
                </Box>
                <Typography variant='h6' color='primary' fontWeight='bold' textAlign='center'>
                  {option.title}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  textAlign='center'
                  sx={{ flexGrow: 1 }}>
                  {option.description}
                </Typography>
                <Button
                  className='option-cta'
                  variant='contained'
                  disableElevation
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    mt: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    backgroundColor: ACCENT,
                    transition: 'gap 0.2s ease, background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: ACCENT,
                      filter: 'brightness(0.92)',
                    },
                  }}>
                  Acceder
                </Button>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
};

export default Resources;
